import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Plus,
  IdCardLanyard,
  CalendarOff,
} from "lucide-react";
import AsyncSelect from "react-select/async";
import { toast } from "react-hot-toast";
import * as constantValues from "../../../constants/constants";
import { employeeService } from "../../../services/employeeService";
import * as helperService from "../../../services/helperService";
import { calendarService } from "../../../services/calendarService";

const createEmptyVacation = () => ({
  id: null,
  employee: "",
  type: "",
  start: "",
  end: "",
  days: "",
  status: "",
});

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const HolidayMonthCalendar = ({ year, month, blockedMap }) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const cells = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <h4 className="text-sm font-bold text-gray-900 mb-3 text-center">
        {MONTH_NAMES[month]}
      </h4>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="text-xs font-semibold text-gray-500 py-1">
            {d}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} className="py-1" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isBlocked = blockedMap.has(dateStr);
          const label = blockedMap.get(dateStr);
          return (
            <div
              key={dateStr}
              title={isBlocked ? label || "Holiday" : undefined}
              className={`text-xs rounded py-1 font-medium ${
                isBlocked ? "bg-red-500 text-white" : "text-gray-900"
              }`}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmployeeDetail = ({
  user,
  userPermission,
  isHRManager = false,
  currentEmployeeId,
  onCurrentEmployeeUpdated,
  onEmployeeUpdated,
  onEmployeeDeleted,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const backTo = location.state?.from ?? "/";
  const [employeeData, setEmployeeData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("data");
  const [managerDefaults, setManagerDefaults] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [editingVacationId, setEditingVacationId] = useState(null);
  const [vacationDraft, setVacationDraft] = useState(createEmptyVacation());
  const employeeIdentifier = employeeData?.id;
  const isTerminatedEmployee = employeeData?.status === "Terminated";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await employeeService.getEmployee(id);
        setEmployeeData(data);
        setEditData(data);
      } catch (err) {
        toast.error("Failed to load employee");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleTabChange = async (nextTabId) => {
    if (activeTab === "data" && nextTabId !== "data" && infoDirty) {
      const saveNow = window.confirm(
        "You have unsaved info. Save before leaving?",
      );
      if (saveNow) {
        const ok = await handleSave();
        if (!ok) return;
        setActiveTab(nextTabId);
      } else {
        setIsEditing(false);
        setEditData(employeeData);
        setActiveTab(nextTabId);
      }
      return;
    }
    setActiveTab(nextTabId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setEditData((prev) => {
      const updatedData = { ...prev, [name]: value };

      if (name === "status" && value !== "Terminated") {
        updatedData.termination_date = "";
        updatedData.termination_reason = "";
      }

      return updatedData;
    });
  };

  const handleCancel = () => {
    setEditData(employeeData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const prevIsHRManager = employeeData.is_hr_manager;
    const prevLevel = employeeData.level;
    try {
      const updated = await employeeService.updateEmployee(
        employeeIdentifier,
        editData,
      );
      const merged = { ...updated, vacations: employeeData.vacations ?? [] };
      setEmployeeData(merged);
      setEditData(merged);
      setIsEditing(false);
      toast.success("Employee updated successfully");
      const isSelf = String(employeeIdentifier) === String(currentEmployeeId);
      if (isSelf) {
        await onCurrentEmployeeUpdated?.();
        const hrRevoked =
          prevIsHRManager === "Yes" && updated.is_hr_manager !== "Yes";
        const managerRevoked =
          (prevLevel === "Manager" || prevLevel === "Partner") &&
          updated.level !== "Manager" &&
          updated.level !== "Partner";
        if (hrRevoked || managerRevoked) {
          window.location.reload();
        }
      }
      onEmployeeUpdated?.();
      return true;
    } catch (err) {
      toast.error(err.message || "Failed to update employee");
      return false;
    }
  };

  const handleDelete = async () => {
    if (userPermission !== "masterAccess") {
      toast.error("You do not have permission to delete employees");
      return;
    }

    if (!employeeData?.id) {
      toast.error("Missing employee identifier");
      return;
    }

    const employeeName = employeeData?.name || "this employee";
    if (
      window.confirm(
        `Are you sure you want to delete ${employeeName}? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${employeeName}? Click "Ok" only if you are absolutely sure.`,
      )
    ) {
      try {
        await employeeService.deleteEmployee(employeeData.id);
        onEmployeeDeleted?.(employeeData.id);
        toast.success("Employee deleted successfully");
        navigate(backTo);
      } catch (err) {
        toast.error(err.message || "Failed to delete employee");
      }
    }
  };

  const cancelVacationEdit = () => {
    setEditingVacationId(null);
    setVacationDraft(createEmptyVacation());
  };

  const handleSaveVacation = async () => {
    if (!employeeIdentifier) {
      toast.error("Missing employee identifier");
      return;
    }
    if (!vacationDraft.type.trim()) {
      toast.error("Type is required");
      return;
    }
    if (!vacationDraft.start.trim()) {
      toast.error("Start date is required");
      return;
    }
    if (!vacationDraft.end.trim()) {
      toast.error("End date is required");
      return;
    }
    if (!vacationDraft.days.trim()) {
      toast.error("# of days is required");
      return;
    }
    if (blockedDays.length > 0) {
      const blockedSet = new Set(blockedDays.map((d) => d.date));
      const start = new Date(vacationDraft.start.trim() + "T00:00:00");
      const end = new Date(vacationDraft.end.trim() + "T00:00:00");
      const hits = [];
      const cur = new Date(start);
      while (cur <= end) {
        const str = cur.toISOString().slice(0, 10);
        if (blockedSet.has(str)) {
          const entry = blockedDays.find((d) => d.date === str);
          hits.push(entry?.label ? `${str} (${entry.label})` : str);
        }
        cur.setDate(cur.getDate() + 1);
      }
      if (hits.length > 0) {
        toast.error(
          `Date range includes non-workable days: ${hits.join(", ")}`,
        );
        return;
      }
    }
    const payload = {
      type: vacationDraft.type.trim(),
      start: vacationDraft.start.trim(),
      end: vacationDraft.end.trim(),
      days: vacationDraft.days,
    };
    try {
      if (!vacationDraft.id) {
        await employeeService.createVacation(employeeIdentifier, payload);
        toast.success("Request added");
      } else {
        await employeeService.updateVacation(
          employeeIdentifier,
          vacationDraft.id,
          payload,
        );
        toast.success("Request updated");
      }
      await refreshVacations();
      cancelVacationEdit();
    } catch (error) {
      toast.error("Failed to save request");
      console.error("Request save error:", error);
    }
  };

  const handleDeleteVacation = async (vacation) => {
    if (!employeeIdentifier) {
      toast.error("Missing employee identifier");
      return;
    }
    if (!window.confirm(`Delete request "${vacation.type || "Unnamed"}"?`)) {
      return;
    }
    try {
      await employeeService.deleteRequest(employeeIdentifier, vacation.id);
      if (editingVacationId === vacation.id) {
        cancelVacationEdit();
      }
      await refreshVacations();
      toast.success("Request deleted");
    } catch (error) {
      toast.error("Failed to delete request");
      console.error("Request delete error:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [rows, days] = await Promise.all([
          employeeService.getManagers("", ["Manager", "Partner"]),
          calendarService.getNonworkableDays(),
        ]);
        setManagerDefaults(rows.map((r) => ({ value: r.id, label: r.name })));
        setBlockedDays(days);
      } catch (error) {
        console.error("Initialization error", error);
      }
    })();
  }, []);

  const loadManagerOptions = async (inputValue) => {
    const rows = await employeeService.getManagers(inputValue, [
      "Manager",
      "Partner",
    ]);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const debounce = (fn, delay = 300) => {
    let timeout;
    return (...args) =>
      new Promise((resolve) => {
        clearTimeout(timeout);
        timeout = setTimeout(async () => resolve(await fn(...args)), delay);
      });
  };

  const debouncedLoadManagers = useMemo(() => debounce(loadManagerOptions), []);

  const requestsCount = employeeData?.vacations?.length ?? 0;

  const sortedHolidays = useMemo(
    () => [...blockedDays].sort((a, b) => a.date.localeCompare(b.date)),
    [blockedDays],
  );

  const blockedMap = useMemo(
    () => new Map(blockedDays.map((d) => [d.date, d.label ?? null])),
    [blockedDays],
  );

  const holidayYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear + 1];
  }, []);

  const vacationBalance = useMemo(() => {
    const total =
      (parseFloat(employeeData?.vacation_days_balance) || 0) +
      (parseFloat(employeeData?.floating_holiday_balance) || 0) +
      (parseFloat(employeeData?.sick_days_balance) || 0);
    const used = (employeeData?.vacations ?? [])
      .filter((req) => req.status !== "Rejected")
      .reduce((sum, req) => sum + (parseFloat(req.days) || 0), 0);
    return { pto: total - used };
  }, [employeeData]);

  const tabs = useMemo(() => {
    if (!employeeData) return [];
    return helperService.createEmployeeTabs({
      requestsCount: requestsCount,
    });
  }, [requestsCount, employeeData]);

  const infoDirty = useMemo(() => {
    if (!isEditing || !employeeData) return false;
    return (
      JSON.stringify(editData ?? {}) !== JSON.stringify(employeeData ?? {})
    );
  }, [isEditing, editData, employeeData]);

  const refreshVacations = useCallback(async () => {
    if (!employeeIdentifier) return [];
    try {
      const vacations = await employeeService.getVacations(employeeIdentifier);
      setEmployeeData((prev) =>
        prev ? { ...prev, vacations: vacations } : prev,
      );
      setEditData((prev) => (prev ? { ...prev, vacations: vacations } : prev));
      return vacations;
    } catch (error) {
      console.error("Refresh requests error:", error);
      toast.error("Failed to refresh requests");
      return [];
    }
  }, [employeeIdentifier]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading employee...</p>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Employee not found</p>
      </div>
    );
  }

  const startEditVacation = (vacation = null) => {
    const forcedType = vacationBalance.pto <= 0 ? "Unpaid Leave" : null;
    if (vacation) {
      setEditingVacationId(vacation.id);
      setVacationDraft({
        id: vacation.id,
        employee: user.name,
        type: forcedType ?? vacation.type,
        start: vacation.start,
        end: vacation.end,
        days: vacation.days,
        status: "Pending",
      });
    } else {
      setEditingVacationId("new");
      setVacationDraft({ ...createEmptyVacation(), type: forcedType ?? "" });
    }
  };

  const workFields = [
    { label: "Work Email", value: employeeData.work_email },
    { label: "Work Phone Number", value: employeeData.work_phone },
    {
      label: "Hire Date",
      value: helperService.formatJoinedDate(employeeData.hire_date),
    },
    { label: "Status", value: employeeData.status },
    ...(isTerminatedEmployee
      ? [
          { label: "Termination Date", value: employeeData.termination_date },
          {
            label: "Termination Reason",
            value: employeeData.termination_reason,
          },
        ]
      : []),
    { label: "Job Title", value: employeeData.job_title },
    { label: "Employment Type", value: employeeData.employment_type },
    { label: "Level", value: employeeData.level },
    { label: "Department", value: employeeData.department },
    ...(isHRManager
      ? [{ label: "HR Manager", value: employeeData.is_hr_manager }]
      : []),
    { label: "Manager", value: employeeData.manager_name },
    {
      label: "Vacation Days Balance",
      value: employeeData.vacation_days_balance,
    },
    { label: "Sick Days Balance", value: employeeData.sick_days_balance },
    {
      label: "Floating Holiday Balance",
      value: employeeData.floating_holiday_balance,
    },
  ];

  const personalFields = [
    { label: "Name", value: employeeData.name },
    {
      label: "Date of Birth",
      value: helperService.formatJoinedDate(employeeData.date_of_birth),
    },
    { label: "SSN", value: helperService.formatSSN(employeeData.ssn) },
    { label: "Gender", value: employeeData.gender },
    { label: "Personal Email", value: employeeData.personal_email },
    { label: "Personal Phone Number", value: employeeData.personal_phone },
    { label: "Address", value: employeeData.address },
    { label: "City", value: employeeData.city },
    { label: "State", value: employeeData.state },
    { label: "Zip", value: employeeData.zip },
    { label: "Country", value: employeeData.country },
    {
      label: "Emergency Contact Name",
      value: employeeData.emergency_contact_name,
    },
    {
      label: "Emergency Contact Phone",
      value: employeeData.emergency_contact_phone,
    },
  ];

  const accountingFields = [
    { label: "Compensation Type", value: employeeData.compensation_type },
    { label: "Base Rate", value: employeeData.base_rate },
    { label: "Bank Account", value: employeeData.bank_account },
  ];

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(backTo)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backTo === "/human_resources"
            ? "Back to Human Resources"
            : "Back to Home"}
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={`p-3 rounded-lg ${
                isTerminatedEmployee
                  ? "bg-red-100 text-red-600"
                  : "bg-blue-100 text-blue-600"
              }`}
            >
              <IdCardLanyard className="h-6 w-6" />
            </div>
            <div>
              <h1
                className={`text-3xl font-bold ${
                  isTerminatedEmployee ? "text-red-600" : "text-gray-900"
                }`}
              >
                {employeeData.name}
              </h1>
              <p
                className={`mt-1 ${
                  isTerminatedEmployee ? "text-red-500" : "text-gray-600"
                }`}
              >
                ID {employeeData.id}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center px-4 py-2 rounded-lg text-white transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
                {isHRManager && (
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-x-6 gap-y-2 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Work Information Tab */}
          {activeTab === "data" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Work Information
              </h3>

              {isEditing && isHRManager ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      name="work_email"
                      value={editData.work_email || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Work Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="work_phone"
                      value={editData.work_phone || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Hire Date *
                    </label>
                    <input
                      type="date"
                      name="hire_date"
                      value={editData.hire_date || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Status *
                    </label>
                    <select
                      name="status"
                      value={editData.status || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select status</option>
                      {constantValues.employeeStatusOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editData.status === "Terminated" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-red-600 mb-2">
                          Termination Date *
                        </label>
                        <input
                          type="date"
                          name="termination_date"
                          value={editData.termination_date || ""}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-red-600 mb-2">
                          Termination Reason *
                        </label>
                        <input
                          type="text"
                          name="termination_reason"
                          value={editData.termination_reason || ""}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Employment Type *
                    </label>
                    <select
                      name="employment_type"
                      value={editData.employment_type || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select type</option>
                      {constantValues.employeeTypeOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      name="job_title"
                      value={editData.job_title || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Level *
                    </label>
                    <select
                      name="level"
                      value={editData.level || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select level</option>
                      {constantValues.employeeLevelOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Department *
                    </label>
                    <select
                      name="department"
                      value={editData.department || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select department</option>
                      {constantValues.employeeDepartmentOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isHRManager && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HR Manager
                      </label>
                      <select
                        name="is_hr_manager"
                        value={editData.is_hr_manager || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Manager *
                    </label>
                    <AsyncSelect
                      isClearable
                      cacheOptions
                      defaultOptions={managerDefaults}
                      loadOptions={debouncedLoadManagers}
                      value={
                        editData.manager_id
                          ? managerDefaults.find(
                              (o) =>
                                String(o.value) === String(editData.manager_id),
                            ) ||
                            (editData.manager_name
                              ? {
                                  value: editData.manager_id,
                                  label: editData.manager_name,
                                }
                              : null)
                          : null
                      }
                      placeholder="Select a manager"
                      onChange={(option) =>
                        setEditData((prev) => ({
                          ...prev,
                          manager_id: option ? option.value : null,
                          manager_name: option ? option.label : "",
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vacation Days Balance
                    </label>
                    <input
                      type="number"
                      name="vacation_days_balance"
                      value={editData.vacation_days_balance ?? ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sick Days Balance
                    </label>
                    <input
                      type="number"
                      name="sick_days_balance"
                      value={editData.sick_days_balance ?? ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Floating Holiday Balance
                    </label>
                    <input
                      type="number"
                      name="floating_holiday_balance"
                      value={editData.floating_holiday_balance ?? ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {workFields.map((field) => (
                    <div key={field.label}>
                      <dt className="text-sm font-medium text-gray-500">
                        {field.label}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {helperService.formatValue(field.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {/* Personal Tab */}
          {activeTab === "personal" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Personal Information
              </h3>

              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editData.name || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={editData.date_of_birth || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      SSN *
                    </label>
                    <input
                      type="text"
                      name="ssn"
                      value={editData.ssn || ""}
                      onChange={handleInputChange}
                      maxLength={9}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={editData.gender || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select gender</option>
                      {constantValues.employeeGenderOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personal Email
                    </label>
                    <input
                      type="email"
                      name="personal_email"
                      value={editData.personal_email || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personal Phone Number
                    </label>
                    <input
                      type="tel"
                      name="personal_phone"
                      value={editData.personal_phone || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={editData.address || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={editData.city || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <select
                      name="state"
                      value={editData.state || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select state</option>
                      {constantValues.usStateOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zip
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={editData.zip || ""}
                      onChange={handleInputChange}
                      maxLength={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={editData.country || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Name
                    </label>
                    <input
                      type="text"
                      name="emergency_contact_name"
                      value={editData.emergency_contact_name || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Phone
                    </label>
                    <input
                      type="tel"
                      name="emergency_contact_phone"
                      value={editData.emergency_contact_phone || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {personalFields.map((field) => (
                    <div key={field.label}>
                      <dt className="text-sm font-medium text-gray-500">
                        {field.label}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {helperService.formatValue(field.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {/* Accounting Tab */}
          {activeTab === "accounting" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Accounting Information
              </h3>

              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Compensation Type
                    </label>
                    <select
                      name="compensation_type"
                      value={editData.compensation_type || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select type</option>
                      {constantValues.employeeCompensationOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Base Rate
                    </label>
                    <input
                      type="number"
                      name="base_rate"
                      value={editData.base_rate || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Bank Account
                    </label>
                    <input
                      type="text"
                      name="bank_account"
                      value={editData.bank_account || ""}
                      onChange={handleInputChange}
                      maxLength={16}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {accountingFields.map((field) => (
                    <div key={field.label}>
                      <dt className="text-sm font-medium text-gray-500">
                        {field.label}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {helperService.formatValue(field.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {/* Vacations Tab */}
          {activeTab === "vacations" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Vacation Requests
                  </h3>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full border ${vacationBalance.pto < 0 ? "bg-red-50 border-red-300 text-red-600" : "bg-blue-50 border-blue-200 text-blue-700"}`}
                  >
                    Available PTO: {vacationBalance.pto}d
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => startEditVacation()}
                  disabled={Boolean(editingVacationId)}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    editingVacationId
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-dashed border-gray-300 text-gray-700 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Request Vacation
                </button>
              </div>

              {editingVacationId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {vacationDraft.id ? "Edit Request" : "New Request"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-red-600 mb-1">
                        Type *
                      </label>
                      <select
                        name="type"
                        value={vacationDraft.type}
                        onChange={(e) =>
                          setVacationDraft((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select type</option>
                        {(vacationBalance.pto <= 0
                          ? constantValues.vacationTypeOptions.filter(
                              (t) => t.value === "Unpaid Leave",
                            )
                          : constantValues.vacationTypeOptions
                        ).map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      {vacationBalance.pto <= 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          PTO balance is negative — only Unpaid Leave is
                          available.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-red-600 mb-1">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        name="start"
                        value={vacationDraft.start}
                        onChange={(e) =>
                          setVacationDraft((prev) => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      {vacationDraft.start &&
                        blockedDays.some(
                          (d) => d.date === vacationDraft.start,
                        ) && (
                          <p className="text-xs text-red-600 mt-1">
                            Non-workable day:{" "}
                            {blockedDays.find(
                              (d) => d.date === vacationDraft.start,
                            )?.label || "Holiday"}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-red-600 mb-1">
                        End Date *
                      </label>
                      <input
                        type="date"
                        name="end"
                        value={vacationDraft.end}
                        onChange={(e) =>
                          setVacationDraft((prev) => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      {vacationDraft.end &&
                        blockedDays.some(
                          (d) => d.date === vacationDraft.end,
                        ) && (
                          <p className="text-xs text-red-600 mt-1">
                            Non-workable day:{" "}
                            {blockedDays.find(
                              (d) => d.date === vacationDraft.end,
                            )?.label || "Holiday"}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-red-600 mb-1">
                        # of Days *
                      </label>
                      <input
                        type="float"
                        name="days"
                        value={vacationDraft.days}
                        onChange={(e) =>
                          setVacationDraft((prev) => ({
                            ...prev,
                            days: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelVacationEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveVacation}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Save Request
                    </button>
                  </div>
                </div>
              )}

              {employeeData.vacations.length > 0 ? (
                <div className="space-y-4">
                  {employeeData.vacations.map((vacation) => {
                    if (editingVacationId === vacation.id) return null;
                    const label = vacation.type?.trim() || "Unnamed entity";
                    return (
                      <div
                        key={vacation.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">
                              {label}
                            </h4>
                          </div>
                          <div className="flex items-center gap-2">
                            {(!vacation.status ||
                              vacation.status === "Pending") && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEditVacation(vacation)}
                                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteVacation(vacation)}
                                  className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <dl className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Start Date
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                              {helperService.formatJoinedDate(vacation.start)}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              End Date
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                              {helperService.formatJoinedDate(vacation.end)}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              # of Days
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                              {vacation.days}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Status
                            </dt>
                            <dd
                              className={`mt-1 text-sm font-medium flex items-center gap-2 ${
                                vacation.status === "Approved"
                                  ? "text-green-600"
                                  : vacation.status === "Rejected"
                                    ? "text-red-600"
                                    : "text-gray-900"
                              }`}
                            >
                              {vacation.status}
                            </dd>
                          </div>
                          {vacation.approver_name && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                {vacation.status === "Rejected"
                                  ? "Rejected By"
                                  : "Approved By"}
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {vacation.approver_name}
                              </dd>
                            </div>
                          )}
                          {vacation.status === "Rejected" && vacation.notes && (
                            <div className="md:col-span-3">
                              <dt className="text-sm font-medium text-gray-500">
                                Rejection Reason
                              </dt>
                              <dd className="mt-1 text-sm text-red-700">
                                {vacation.notes}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">No requests stored</p>
              )}
            </div>
          )}

          {/* Holidays Tab */}
          {activeTab === "holidays" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <CalendarOff className="h-5 w-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Available Holidays
                </h3>
              </div>

              {sortedHolidays.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {sortedHolidays.map((holiday) => (
                    <div
                      key={holiday.date}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full text-xs font-semibold text-red-700"
                    >
                      {helperService.formatJoinedDate(holiday.date)}
                      {holiday.label && (
                        <span className="text-red-500">— {holiday.label}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No holidays registered</p>
              )}

              {holidayYears.map((year) => (
                <div key={year}>
                  <h4 className="text-base font-semibold text-gray-900 mb-4">
                    {year}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 12 }, (_, month) => (
                      <HolidayMonthCalendar
                        key={month}
                        year={year}
                        month={month}
                        blockedMap={blockedMap}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetail;
